import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Send, Check } from 'lucide-react';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    setIsSubmitted(true);
  };

  return (
    <div className="py-12" data-testid="contact-page">
      <div className="container-wide">
        <Breadcrumbs items={[{ label: 'Kontakt', href: '/kontakt' }]} />

        <div className="max-w-2xl">
          <header className="mb-12">
            <h1 className="mb-6">Kontaktieren Sie uns</h1>
            <p className="text-xl text-slate-600">
              Haben Sie Fragen zu unseren Ratgebern oder möchten uns Feedback geben? 
              Wir freuen uns von Ihnen zu hören.
            </p>
          </header>

          {isSubmitted ? (
            <div className="card-highlight p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-600 rounded-full">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4">
                Nachricht gesendet!
              </h2>
              <p className="text-slate-600 mb-6">
                Vielen Dank für Ihre Nachricht. Wir werden uns innerhalb von 24–48 Stunden 
                bei Ihnen melden.
              </p>
              <Button
                onClick={() => setIsSubmitted(false)}
                variant="outline"
              >
                Neue Nachricht senden
              </Button>
            </div>
          ) : (
            <div className="card-flat p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ihr Name"
                    data-testid="contact-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="ihre@email.de"
                    data-testid="contact-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Betreff</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Worum geht es?"
                    data-testid="contact-subject"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Nachricht</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Ihre Nachricht..."
                    data-testid="contact-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full cta-primary"
                  data-testid="contact-submit"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Nachricht senden
                </Button>
              </form>
            </div>
          )}

          <div className="mt-12 space-y-6">
            <div className="card-flat p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-sm flex-shrink-0">
                  <Mail className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-navy-900 mb-1">E-Mail</h3>
                  <p className="text-slate-600">
                    <a 
                      href="mailto:kontakt@reviewlex.de" 
                      className="text-teal-600 hover:text-teal-700"
                    >
                      kontakt@reviewlex.de
                    </a>
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Wir antworten in der Regel innerhalb von 24–48 Stunden.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-6">
              <h4 className="font-heading font-bold text-navy-900 mb-2">Wichtiger Hinweis</h4>
              <p className="text-slate-700 text-sm">
                Bitte beachten Sie, dass wir <strong>keine Rechts- oder Steuerberatung</strong> anbieten. 
                Für individuelle rechtliche Fragen wenden Sie sich bitte an einen qualifizierten 
                Anwalt oder Steuerberater.
              </p>
            </div>

            <p className="text-slate-600 text-sm">
              Vielleicht finden Sie die Antwort bereits in unseren{' '}
              <Link to="/ratgeber" className="text-teal-600 hover:text-teal-700">
                Ratgebern
              </Link>{' '}
              oder auf den FAQ-Seiten unserer Hauptartikel.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
